import { DateTime } from 'luxon';
import { Megaphone } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import type {
  OrdersDrilldownCustomersResponseDto,
  OrdersDrilldownOrdersResponseDto,
} from '@/generated/korza-api-client/types.gen';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/components/metrics/formatters';
import {
  CHANNEL_MIGRATION_COLOR,
  FIRST_PURCHASE_COLOR,
  IN_APP_REPURCHASE_COLOR,
} from '@/components/orders-composition-donut';
import { CustomerAvatar } from '@/components/orders-drilldown/customer-avatar';
import { OrdersDrilldownCampaignCell } from '@/components/orders-drilldown/orders-drilldown-campaign-cell';
import { Badge } from '@/components/ui/badge';

type OrderItem = OrdersDrilldownOrdersResponseDto['items'][number];
type CustomerItem = OrdersDrilldownCustomersResponseDto['items'][number];

const KIND_COLORS: Record<OrderItem['kind'], string> = {
  in_app: IN_APP_REPURCHASE_COLOR,
  channel_migration: CHANNEL_MIGRATION_COLOR,
  first_purchase: FIRST_PURCHASE_COLOR,
};

const CARD_CLASS = cn(
  'flex flex-col gap-1.5 rounded-lg border bg-card p-4 text-card-foreground',
  mobileLayer.card
);

function hasText(value: string | null | undefined): value is string {
  return Boolean(value?.trim());
}

interface OrdersDrilldownOrderCardProps {
  item: OrderItem;
  /** Shows the order type badge (repurchase kinds). */
  isRepurchase: boolean;
}

/** One order of the mobile drill-down sheet (SALES-06 AC 3). */
export function OrdersDrilldownOrderCard({
  item,
  isRepurchase,
}: OrdersDrilldownOrderCardProps) {
  const { LL, locale } = useI18nContext();
  const kindLabels: Record<OrderItem['kind'], string> = {
    in_app: LL.metricsPage.ordersComposition.inAppRepurchase.label(),
    channel_migration:
      LL.metricsPage.ordersComposition.channelMigration.label(),
    first_purchase: LL.metricsPage.ordersComposition.firstPurchase.label(),
  };

  return (
    <div data-drilldown-order-card className={CARD_CLASS}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold">
          {hasText(item.orderNumber) ? item.orderNumber : '—'}
        </span>
        <span className="font-semibold tabular-nums">
          {formatCurrency(item.totalAmount, locale, item.currency)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
        <span className="min-w-0 truncate">
          <span className="tabular-nums">
            {DateTime.fromISO(item.date).toFormat('HH:mm')}
          </span>
          {hasText(item.customerName) ? ` · ${item.customerName}` : null}
        </span>
        {isRepurchase ? (
          <Badge
            variant="outline"
            className="shrink-0 gap-1.5 whitespace-nowrap"
          >
            <span
              className="inline-block size-2 rounded-full"
              style={{ backgroundColor: KIND_COLORS[item.kind] }}
              aria-hidden="true"
            />
            {kindLabels[item.kind]}
          </Badge>
        ) : null}
      </div>
      <div className="flex items-start gap-2 text-sm">
        <Megaphone
          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
        <OrdersDrilldownCampaignCell campaign={item.campaign} />
      </div>
    </div>
  );
}

interface OrdersDrilldownCustomerCardProps {
  item: CustomerItem;
}

/** One customer of the mobile drill-down sheet (Clientes view). */
export function OrdersDrilldownCustomerCard({
  item,
}: OrdersDrilldownCustomerCardProps) {
  const { LL, locale } = useI18nContext();
  const secondary = hasText(item.email)
    ? item.email
    : hasText(item.phone)
      ? item.phone
      : null;
  const primary = hasText(item.name) ? item.name : secondary;

  return (
    <div
      data-drilldown-customer-card
      className={cn(CARD_CLASS, 'flex-row items-center gap-3')}
    >
      <CustomerAvatar name={primary} seed={secondary} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">
          {primary ?? LL.metricsPage.repurchaseDrilldown.unidentifiedCustomer()}
        </div>
        <div className="text-sm text-muted-foreground">
          {LL.metricsPage.repurchaseDrilldown.mobile.customerOrders({
            count: item.orderCount,
          })}
        </div>
      </div>
      <span className="shrink-0 font-semibold tabular-nums">
        {formatCurrency(item.totalSpent, locale, item.currency)}
      </span>
    </div>
  );
}
